/**
 * The Chart widget body for every chart type (screenshots/75 gallery, 71 sizes, 74 interactions).
 * Every chart — including the S-size sparkline — renders with Apache ECharts 6 (modular core build):
 * init once per mount, then setOption on data/size/type changes. Only the Table type is plain HTML.
 *
 * Motion (motion.ts recipes):
 * - chartEnter: marks grow from 0 ONLY when the widget was just added (`justAdded`). First paint from the snapshot never animates.
 * - chartMorph: live updates merge by series id, so bars/lines/graph nodes morph to new values.
 * - sizeCrossfade: changing type or crossing S/M/L swaps the option (notMerge) behind a 150ms crossfade.
 * - Clusters: steps through the k-means iterations with setOption + universalTransition (skipped under reduced motion).
 *
 * Settings + themes (screenshots/76–80): `settings` (chartSettings.ts) is layered onto the option with
 * `applySettings`; `theme` swaps the neutral palette slots and is applied with ECharts 6 `chart.setTheme()`,
 * so a theme change recolors in place instead of re-initializing.
 */
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { useThemeVersion } from "../../theme/runtime";
import * as echarts from "echarts/core";
import { BarChart, GraphChart, HeatmapChart, LineChart, MapChart, PieChart, RadarChart, SankeyChart, ScatterChart, TreeChart, TreemapChart } from "echarts/charts";
import { AriaComponent, CalendarComponent, DataZoomInsideComponent, DataZoomSliderComponent, GridComponent, LegendComponent, MarkLineComponent, MarkPointComponent, MatrixComponent, RadarComponent,
  TitleComponent, TooltipComponent, VisualMapComponent } from "echarts/components";
import { LabelLayout, UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import { prefersReducedMotion, recipes } from "../../motion";
import { buildOption, readPalette } from "./chartOptions";
import { applySettings, buildTable, defaultTable, type ChartSettings, type TableRow, type TableSettings } from "./chartSettings";
import { UI_CHART_THEME, paletteFromTheme, type ChartTheme } from "./themes";
import { registerChartTheme } from "./themeStore";
import { loadWardBoundaries, wardBoundariesReady } from "../../geo/wards";
import { CHART_TYPES, headline, kmeansSteps, sizePlan, toTable, widgetSize, type ChartConfig, type ChartType, type Shape } from "./chartTypes";

echarts.use([BarChart, GraphChart, HeatmapChart, LineChart, MapChart, PieChart, RadarChart, SankeyChart, ScatterChart, TreeChart, TreemapChart,
  AriaComponent, CalendarComponent, DataZoomInsideComponent, DataZoomSliderComponent, GridComponent, LegendComponent, MarkLineComponent, MarkPointComponent, MatrixComponent, RadarComponent,
  TitleComponent, TooltipComponent, VisualMapComponent, LabelLayout, UniversalTransition, CanvasRenderer]);

/**
 * Real ward boundaries for the Ward map: call once at startup with the City of Chicago ward boundaries GeoJSON
 * (each feature needs a `ward` property, "1"–"50"), then pass `config.wardGeo = WARD_MAP` to Ward map widgets.
 * Without it, the Ward map uses the tile grid (layouts.ts).
 */
export const WARD_MAP = "chicago-wards";

/**
 * Canvas text doesn't load web fonts by itself, and ECharts caches label widths per font: measuring "12px Inter" before
 * Inter has loaded lays labels out with the fallback's widths and clips them once Inter draws (themes set Inter).
 * So the first setOption waits for the label weights to load (a few ms on first paint, instant afterwards).
 */
let fontsLoaded = typeof document === "undefined" || !document.fonts;
const fontsReady: Promise<unknown> = fontsLoaded ? Promise.resolve()
  : Promise.all(["12px Inter", "600 12px Inter", "500 11px Inter"].map((f) => document.fonts.load(f))).catch(() => null).then(() => { fontsLoaded = true; });
export const registerWardBoundaries = (geojson: object) => echarts.registerMap(WARD_MAP, geojson as Parameters<typeof echarts.registerMap>[1]);

export interface ChartWidgetProps {
  type: ChartType;
  shape: Shape;
  /** The widget's own grid cells (sizes come from these, not the viewport). */
  w: number; h: number;
  title: string;
  /** Footer: "Sample data" while a dataset is sample, otherwise the source line. */
  source: { kind: "real"; text: string } | { kind: "sample" };
  /** Variant settings: tree direction / polyline edges, k for Clusters, ward GeoJSON. */
  config?: ChartConfig;
  justAdded?: boolean;
  /** Labels / colors / interactions from the chart settings popup. */
  settings?: ChartSettings;
  /** Row extras for label tokens, e.g. { nugent: { ward: 39 } }. */
  vars?: Record<string, Record<string, string | number>>;
  /** The widget's chart theme (registered with ECharts on first use). */
  theme?: ChartTheme;
  /** Click-through: the same row keys the dataset returned (bar → member page, ward → ward, tree node → item, etc.). */
  onSelect?: (key: string) => void;
  /** Chart settings preview (81 #5): a click on a category axis label, with its position inside the canvas. */
  onLabelClick?: (label: string, x: number, y: number) => void;
  /** Zoom & brush moved (time charts): % range, saved in config.zoom and the dashboard link (screenshots/89). */
  onZoom?: (range: [number, number]) => void;
  /** Gives the parent the live ECharts instance (⋯ menu → Download PNG/SVG). */
  chartRef?: MutableRefObject<echarts.ECharts | null>;
  /** Signed-in viewer, for table highlight rules like "Ward = {viewer.ward}". */
  viewer?: Record<string, string | number>;
  /** Table total row: the dataset's full total, not just the rows shown (87: "$45,850 to 22 alderpersons"). */
  allRowsTotal?: number;
}

export function ChartWidget(props: ChartWidgetProps) {
  const { type, shape, w, h, title, source } = props;
  const size = widgetSize(w, h);
  const plan = sizePlan(type, size);
  const [asTable, setAsTable] = useState(false);
  const mode = type === "table" || asTable ? "table" : plan.render === "headline" || type === "number" ? "headline" : "chart";

  return (
    <figure className={`chart-widget chart-widget--${size}`} data-type={type} aria-label={`${title}, ${CHART_TYPES[type].label.toLowerCase()} chart`}>
      <div className="chart-widget__body" key={`${mode}:${type}:${size}`} /* key change → sizeCrossfade */>
        {mode === "table" && (type === "table" && props.settings?.table
          ? <SettingsTable shape={shape} settings={props.settings} vars={props.vars} viewer={props.viewer} allRowsTotal={props.allRowsTotal} onSelect={props.onSelect} size={size} />
          : <ChartTable shape={shape} maxRows={plan.maxRows} onSelect={props.onSelect} />)}
        {mode === "headline" && <HeadlineView shape={shape} />}
        {mode === "chart" && <EChart {...props} />}
        {mode === "chart" && (type === "bar" || type === "column") && shape.kind === "category" && shape.rows.length > plan.maxRows
          && <span className="chart-widget__more">+{shape.rows.length - plan.maxRows} more</span>}
      </div>
      <figcaption className="chart-widget__foot">
        {source.kind === "real" && <span>{source.text}</span>}
        {plan.tableToggle && type !== "table" && (
          <button className="link-button" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>{asTable ? "Show as chart" : "Show as table"}</button>
        )}
      </figcaption>
    </figure>
  );
}

/** The one ECharts host. Owns init/dispose/resize/click/theme; callers only hand it options. */
export function useECharts(el: RefObject<HTMLDivElement | null>, onClick?: (key: string) => void, theme?: ChartTheme) {
  const chart = useRef<echarts.ECharts | null>(null);
  const clickRef = useRef(onClick);
  clickRef.current = onClick;
  useEffect(() => {
    if (theme) registerChartTheme(theme);
    const c = echarts.init(el.current!, theme?.id ?? null, { renderer: "canvas" });
    chart.current = c;
    c.on("click", (p: { name?: string; data?: unknown }) => {
      const d = (p.data ?? {}) as { key?: string; id?: string; value?: unknown[] };
      const key = d.key ?? d.id ?? (Array.isArray(d.value) && d.value.length === 5 ? String(d.value[4]) : p.name);   // tree/graph/ward tile/others
      if (key) clickRef.current?.(String(key));
    });
    const ro = new ResizeObserver(() => c.resize());
    ro.observe(el.current!);
    return () => { ro.disconnect(); c.dispose(); chart.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live theme switch (ECharts 6): recolors axes, tooltips and series defaults in place.
  // The default chart theme follows the dashboard theme (Settings → Appearance): re-register it when the colors change.
  const themeVersion = useThemeVersion();
  const themeKey = theme ? `${theme.id}:${theme.updatedAt ?? ""}:${theme.id === UI_CHART_THEME ? themeVersion : ""}` : "";
  const firstTheme = useRef(true);
  useEffect(() => {
    if (firstTheme.current) { firstTheme.current = false; return; }
    if (!theme || !chart.current) return;
    registerChartTheme(theme);
    chart.current.setTheme(theme.id);
  }, [themeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return chart;
}

function EChart({ type, shape, w, h, title, config: given, justAdded, onSelect, settings, vars, theme, onLabelClick, onZoom, chartRef }: ChartWidgetProps) {
  // Ward map: the City's real ward boundaries (geo/wards.ts, /data/wards.geojson), the tile grid until they load.
  const [wardsOk, setWardsOk] = useState(wardBoundariesReady);
  useEffect(() => { if (type === "wardMap" && !wardsOk) void loadWardBoundaries(registerWardBoundaries).then((ok) => { if (ok) setWardsOk(true); }); }, [type, wardsOk]);
  const config = useMemo(() => (type === "wardMap" && wardsOk && !given?.wardGeo ? { ...given, wardGeo: WARD_MAP } : given), [type, wardsOk, given]);
  const el = useRef<HTMLDivElement>(null);
  const chart = useECharts(el, onSelect, theme);
  const labelClick = useRef(onLabelClick);
  labelClick.current = onLabelClick;
  const zoomRef = useRef(onZoom);
  zoomRef.current = onZoom;
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    if (chartRef) chartRef.current = c;
    const h = (p: { targetType?: string; value?: unknown; event?: { offsetX?: number; offsetY?: number } }) => {
      if (p.targetType === "axisLabel" && labelClick.current) labelClick.current(String(p.value), p.event?.offsetX ?? 0, p.event?.offsetY ?? 0);
    };
    const onDz = () => { const dz = (c.getOption() as { dataZoom?: { start: number; end: number }[] }).dataZoom?.[0]; if (dz) zoomRef.current?.([dz.start, dz.end]); };
    c.on("click", h as never);
    c.on("datazoom", onDz);
    return () => { if (!c.isDisposed()) { c.off("click", h as never); c.off("datazoom", onDz); } if (chartRef) chartRef.current = null; };   // the host may have disposed first
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const last = useRef<{ type: ChartType; size: string } | null>(null);
  const size = widgetSize(w, h);
  const plan = useMemo(() => sizePlan(type, size), [type, size]);
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const palette = theme ? paletteFromTheme(readPalette(), theme) : readPalette();
    // A type that can't draw this shape (mid-switch between datasets) draws nothing instead of taking the page down.
    let built: ReturnType<typeof buildOption>;
    try { built = buildOption(type, shape, plan, palette, false, config); } catch { return { series: [] } as ReturnType<typeof buildOption>; }
    if (!settings) return built;
    const keyByLabel = new Map(rowsOf(shape).map((r) => [r.label, r.key]));
    const applied = applySettings(built, settings, { palette, unit: "unit" in shape && shape.unit ? shape.unit : "count", vars,
      keyOf: (l) => keyByLabel.get(l) ?? l, reducedMotion: prefersReducedMotion() }) as { xAxis?: unknown; yAxis?: unknown };
    // Category axis labels become clickable for in-place renames (81 #5).
    if (onLabelClick) for (const k of ["xAxis", "yAxis"] as const) {
      const axes = applied[k];
      for (const ax of (Array.isArray(axes) ? axes : axes ? [axes] : []) as { type?: string; triggerEvent?: boolean }[]) if (ax.type === "category") ax.triggerEvent = true;
    }
    return applied as ReturnType<typeof applySettings>;
  }, [type, shape, plan, config, settings, vars, theme, !!onLabelClick, themeVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const [fontsOk, setFontsOk] = useState(fontsLoaded);
  useEffect(() => { if (!fontsOk) void fontsReady.then(() => setFontsOk(true)); }, [fontsOk]);
  useEffect(() => {
    const c = chart.current;
    if (!c || !fontsOk) return;
    const reduced = prefersReducedMotion();
    const first = last.current === null;
    const swapped = !first && (last.current!.type !== type || last.current!.size !== size);
    const animate = !reduced && (first ? !!justAdded : !swapped) && (option as { animation?: boolean }).animation !== false;
    c.setOption({
      ...option,
      animation: animate,
      animationDuration: settings ? settings.interact.duration : recipes.chartEnter.duration,
      animationDurationUpdate: settings ? (settings.interact.update === "none" ? 0 : settings.interact.duration) : recipes.chartMorph.duration,
      aria: { enabled: true, label: { description: `${title}. Use "Show as table" for exact values.` } },
    }, { notMerge: first || swapped, lazyUpdate: true });
    last.current = { type, size };

    // "Clustering process": replay k-means iterations once, merging by series id so points glide between groups.
    if (type === "clustering" && shape.kind === "xy" && !reduced && config?.clusterStep === undefined) {
      const steps = kmeansSteps(shape.points, config?.clusters ?? 3).length;
      let i = 0;
      const timer = window.setInterval(() => {
        if (i >= steps) return window.clearInterval(timer);
        c.setOption(buildOption(type, shape, plan, readPalette(), true, { ...config, clusterStep: i++ }), { lazyUpdate: true });
      }, 900);
      return () => window.clearInterval(timer);
    }
  }, [option, fontsOk]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={el} className="chart-widget__canvas" role="img" aria-label={title} />;
}

function HeadlineView({ shape }: { shape: Shape }) {
  const hl = headline(shape);
  return (
    <div className="chart-headline">
      <span className="chart-headline__value">{hl.value}</span>
      <span className="chart-headline__label">{hl.label}</span>
      {hl.delta && <span className={`chart-headline__delta is-${hl.delta.dir}`}>{hl.delta.text}</span>}
      {shape.kind === "time" && shape.points.length > 1 && <Sparkline shape={shape} />}
    </div>
  );
}

/** Sparkline: the Number type's ECharts option (series.line, axes hidden) in a 36px-tall host. */
export function Sparkline({ shape }: { shape: Extract<Shape, { kind: "time" }> }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useECharts(el);
  const themeVersion = useThemeVersion();
  useEffect(() => {
    chart.current?.setOption({ ...buildOption("number", shape, sizePlan("number", "S"), readPalette(), false), animation: false }, { lazyUpdate: true });
  }, [shape, themeVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={el} className="sparkline" aria-hidden />;
}

/** Table type + "Show as table". Click a header to sort; the first column links when rows have keys. */
export function ChartTable({ shape, maxRows, onSelect }: { shape: Shape; maxRows: number; onSelect?: (key: string) => void }) {
  const t = toTable(shape);
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);
  const rows = useMemo(() => {
    if (!sort) return t.rows;
    const num = (v: string | number) => (typeof v === "number" ? v : Number(String(v).replace(/[^0-9.-]/g, "")) || 0);
    return [...t.rows].sort((a, b) => {
      const x = a[sort.col], y = b[sort.col];
      const d = typeof x === "number" || /\d/.test(String(x)) ? num(x) - num(y) : String(x).localeCompare(String(y));
      return d * sort.dir;
    });
  }, [t, sort]);
  const shown = rows.slice(0, maxRows);
  const keys = rowKeys(shape);
  return (
    <div className="chart-table">
      <table>
        <thead><tr>{t.columns.map((c, i) => (
          <th key={i} aria-sort={sort?.col === i ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button onClick={() => setSort((s) => ({ col: i, dir: s?.col === i && s.dir === -1 ? 1 : -1 }))}>{c}</button>
          </th>))}</tr></thead>
        <tbody>{shown.map((r, i) => {
          const key = keys.get(String(r[0]));
          return <tr key={i} onClick={key && onSelect ? () => onSelect(key) : undefined} className={key && onSelect ? "is-link" : undefined}>
            {r.map((c, j) => <td key={j}>{c}</td>)}</tr>;
        })}</tbody>
      </table>
      {rows.length > shown.length && <span className="chart-table__more">+{rows.length - shown.length} more</span>}
    </div>
  );
}

const rowKeys = (s: Shape) => new Map<string, string>(
  s.kind === "category" || s.kind === "signed" ? s.rows.map((r) => [r.label, r.key])
  : s.kind === "parts" ? s.parts.map((r) => [r.label, r.key])
  : s.kind === "xy" ? s.points.map((r) => [r.label, r.key])
  : s.kind === "members" ? s.members.map((m) => [m.name, m.personId])
  : s.kind === "ward" ? Object.keys(s.values).map((w) => [`Ward ${w}`, w])
  : s.kind === "network" ? s.nodes.map((n) => [n.label, n.href ?? n.id])
  : s.kind === "events" ? s.events.filter((e) => e.href).map((e) => [e.date, e.href!])
  : []);

/** Rows (key + label) for label/color lookups across shapes. */
const rowsOf = (s: Shape): { key: string; label: string }[] =>
  s.kind === "category" || s.kind === "signed" ? s.rows : s.kind === "parts" ? s.parts
  : s.kind === "xy" ? s.points : s.kind === "members" ? s.members.map((m) => ({ key: m.personId, label: m.name }))
  : s.kind === "network" ? s.nodes.map((n) => ({ key: n.id, label: n.label })) : [];

/** Table type with column/format/highlight settings (screenshots/87). */
function SettingsTable({ shape, settings, vars, viewer, allRowsTotal, onSelect, size }: { shape: Shape; settings: ChartSettings; vars?: Record<string, Record<string, string | number>>;
  viewer?: Record<string, string | number>; allRowsTotal?: number; onSelect?: (key: string) => void; size: string }) {
  const t = settings.table ?? defaultTable();
  const rows: TableRow[] = rowsOf(shape).map((r) => ({ key: r.key, values: { label: r.label, value: "value" in r ? (r as { value: number }).value : undefined, ...(vars?.[r.key] ?? {}) } }));
  // Sizes (71, §2.11): S stays a table with 3 rows and 2 columns, M shows at most 5 rows; L follows the settings.
  const shown = size === "S" ? { ...t, rows: 3 as unknown as TableSettings["rows"], totalRow: false, columns: t.columns.filter((c) => c.visible).slice(0, 2) }
    : size === "M" ? { ...t, rows: (t.rows === "all" || t.rows > 5 ? 5 : t.rows) as TableSettings["rows"] } : t;
  const built = buildTable(rows, shown,
    { unit: "unit" in shape && shape.unit ? shape.unit : "count", viewer, allRowsTotal });
  return (
    <div className={`chart-table chart-table--${t.density}${t.freezeFirst ? " is-frozen" : ""}`}>
      <table>
        <thead><tr>{built.columns.map((c) => <th key={c.field} aria-sort={t.sortBy?.field === c.field ? (t.sortBy.dir === "asc" ? "ascending" : "descending") : "none"}>{c.label}</th>)}</tr></thead>
        <tbody>{built.rows.map((r) => (
          <tr key={r.key} className={r.style ? `is-${r.style}` : undefined} onClick={onSelect ? () => onSelect(r.key) : undefined}>
            {r.cells.map((c, j) => <td key={j}>{c.bar !== undefined ? <span className="cell-bar"><i style={{ width: `${c.bar * 100}%` }} />{c.text}</span>
              : j === 0 && onSelect ? <a href={`#${r.key}`} onClick={(e) => { e.preventDefault(); onSelect(r.key); }}>{c.text}</a> : c.text}</td>)}
          </tr>))}</tbody>
        {built.total && <tfoot><tr>{built.total.cells.map((c, j) => <td key={j}>{c ?? ""}</td>)}</tr></tfoot>}
      </table>
      {built.more > 0 && <span className="chart-table__more">View all {built.rows.length + built.more} →</span>}
    </div>
  );
}
